import React from "react";
import { Star } from "lucide-react";
import {
  EBAY_FEEDBACK_SOURCE_URL,
  EBAY_POSITIVE_COUNT,
  EBAY_POSITIVE_PCT_LAST_12,
  EBAY_SELLER_CRITERIA,
} from "../../lib/ebayReviews";

/**
 * Aggregate eBay DSR-style criteria + positive count.
 * Scores come from the seller profile, not invented per review.
 */
export default function EbaySellerCriteriaStrip({ className = "" }) {
  return (
    <section
      className={`rounded-[1.35rem] border border-white/12 bg-gradient-to-br from-white/[0.07] via-panel/80 to-void/70 px-5 py-6 sm:px-7 sm:py-7 ${className}`}
      aria-labelledby="ebay-criteria-heading"
    >
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="home-eyebrow m-0 mb-2">Seller ratings</p>
          <h2
            id="ebay-criteria-heading"
            className="font-sans font-semibold text-xl md:text-2xl text-ivory m-0 tracking-tight"
          >
            How buyers rate Moss Matrix on eBay
          </h2>
          <p className="mt-2 text-sm text-sage m-0 max-w-xl leading-relaxed">
            Aggregate detailed seller ratings from the eBay profile — not per-review scores.
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl md:text-4xl font-sans font-semibold text-gold tabular-nums m-0 leading-none">
            {EBAY_POSITIVE_COUNT}
          </p>
          <p className="text-[11px] text-sage/80 m-0 mt-1.5 max-w-[11rem] ml-auto leading-snug">
            positive in last 12 months
            {EBAY_POSITIVE_PCT_LAST_12 != null ? ` · ${EBAY_POSITIVE_PCT_LAST_12}%` : ""}
          </p>
        </div>
      </div>

      <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 list-none p-0 m-0">
        {EBAY_SELLER_CRITERIA.map((row) => (
          <li
            key={row.id}
            className="rounded-2xl border border-white/10 bg-void/35 px-4 py-4"
          >
            <p className="text-xs text-sage m-0 mb-2 leading-snug">{row.label}</p>
            <div className="flex items-center gap-1" aria-label={`${row.scoreOutOf5} out of 5`}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`h-3.5 w-3.5 ${
                    i < Math.round(row.scoreOutOf5)
                      ? "fill-gold text-gold"
                      : "text-white/20"
                  }`}
                  strokeWidth={1.5}
                  aria-hidden
                />
              ))}
              <span className="ml-1.5 text-sm text-ivory tabular-nums">{row.scoreOutOf5}/5</span>
            </div>
          </li>
        ))}
      </ul>

      <p className="mt-5 text-[11px] text-sage/70 m-0">
        Source:{" "}
        <a
          href={EBAY_FEEDBACK_SOURCE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-white/20 underline-offset-2 hover:text-goldhover"
        >
          eBay feedback profile
        </a>
        . Criteria scores are profile aggregates.
      </p>
    </section>
  );
}
